import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMainPageTitle } from "../../contexts/PageContext";
import useCompetitionRequests from "../../hooks/useCompetitionRequests";
import RequestCard from "../../components/organization/competition-requests/RequestCard";
import ConfirmationDialog from "../../components/organization/competition-requests/ConfirmationDialog";

const CompetitionRequests = () => {
  const { updatePageTitle } = useMainPageTitle();
  const navigate = useNavigate();

  // Use the custom hook for all competition request logic
  const {
    activeTab,
    requests,
    loading,
    error,
    pagination,
    filters,
    handleTabChange,
    handleFilterChange,
    handlePageChange,
    handleCancelRequest,
    handleAcceptRequest,
    handleRejectRequest
  } = useCompetitionRequests();

  // Confirmation dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'cancel', 'accept', 'reject'
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    updatePageTitle("Competition Requests", "Manage competition requests");
  }, [updatePageTitle]);

  const handleCreateRequest = () => {
    navigate('/organization/competition-requests/create');
  };

  const openDialog = (type, requestId) => {
    setDialogType(type);
    setSelectedRequestId(requestId);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setDialogType('');
    setSelectedRequestId(null);
    setRejectReason('');
  };

  const confirmAction = async () => {
    if (!selectedRequestId) return;

    let result;
    switch (dialogType) {
      case 'cancel':
        result = await handleCancelRequest(selectedRequestId);
        break;
      case 'accept':
        result = await handleAcceptRequest(selectedRequestId);
        break;
      case 'reject':
        result = await handleRejectRequest(selectedRequestId, rejectReason);
        break;
      default:
        return;
    }

    // Close dialog
    closeDialog();

    // You could show a toast notification here based on result.success
    if (result.success) {
      console.log(result.message);
    } else {
      console.error(result.message);
    }
  };

  // Loading state
  if (loading && requests.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Competition Requests</h1>
          <p className="text-gray-600 mt-2">Manage and track your competition requests</p>
        </div>
        <button
          onClick={handleCreateRequest}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Request
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('pending')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending Requests
            </button>
            <button
              onClick={() => handleTabChange('ended')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'ended'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Request History
            </button>
          </nav>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Requests</option>
            <option value="sent">Sent by Me</option>
            <option value="received">Received by Me</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Request Type</label>
          <select
            value={filters.requestType}
            onChange={(e) => handleFilterChange('requestType', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Types</option>
            <option value="single_match">Single Match</option>
            <option value="tournament">Tournament</option>
            <option value="league">League</option>
            <option value="friendly">Friendly</option>
            <option value="championship">Championship</option>
          </select>
        </div>
        {activeTab === 'ended' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        )}
      </div>

      {/* Request List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
          <p className="text-gray-500 mb-4">
            {activeTab === 'pending' 
              ? "You don't have any pending competition requests." 
              : "No request history available."}
          </p>
          {activeTab === 'pending' && (
            <button
              onClick={handleCreateRequest}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700"
            >
              Create Your First Request
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 mb-8">
            {requests.map((request) => (
              <RequestCard 
                key={request._id} 
                request={request}
                onCancel={(requestId) => openDialog('cancel', requestId)}
                onAccept={(requestId) => openDialog('accept', requestId)}
                onReject={(requestId) => openDialog('reject', requestId)}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.currentPage - 1) * 20) + 1} to {Math.min(pagination.currentPage * 20, pagination.totalItems)} of {pagination.totalItems} requests
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDialog}
        onClose={closeDialog}
        onConfirm={confirmAction}
        type={dialogType}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
      />
    </div>
  );
};

export default CompetitionRequests; 